import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Megaphone, Copy, Check, Users, Euro, CreditCard, ArrowLeft, Share2, Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import { api, vibrate } from "@/lib/api";
import { APP_URL } from "@/config";

const fmt = (d) => { try { return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); } catch { return "—"; } };

const TIER_COLORS = {
  free: "text-zinc-400", basic: "text-sky-400", pro: "text-pink-400",
  docteur: "text-amber-400", comp: "text-emerald-400",
};

export default function Ambassador() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/ambassador/me")
      .then((r) => setData(r.data))
      .catch(() => setData({ is_ambassador: false }))
      .finally(() => setLoading(false));
  }, []);

  const link = data?.code ? `${APP_URL}/?promo=${data.code}` : "";

  const copyLink = async () => {
    vibrate();
    try { await navigator.clipboard.writeText(link); } catch { /* clipboard blocked */ }
    setCopied(true);
    toast.success("Lien copié 🎉");
    setTimeout(() => setCopied(false), 1600);
  };

  const shareLink = async () => {
    vibrate();
    const text = `Analyse tes conversations sur DocLove 💘 avec mon code ${data.code} : -50% le 1ᵉʳ mois ! ${link}`;
    try {
      if (navigator.share) await navigator.share({ title: "DocLove", text, url: link });
      else { await navigator.clipboard.writeText(text); toast.success("Lien copié 🎉"); }
    } catch (e) { if (e?.name !== "AbortError") { await navigator.clipboard.writeText(text); toast.success("Lien copié"); } }
  };

  if (loading) {
    return <Layout><div className="flex justify-center py-20"><Loader2 className="animate-spin text-pink-400" /></div></Layout>;
  }

  if (!data?.is_ambassador) {
    return (
      <Layout>
        <div className="text-center py-16" data-testid="ambassador-not-eligible">
          <Megaphone className="mx-auto text-zinc-600 mb-3" size={32} />
          <h1 className="font-display text-2xl font-black">Espace Ambassadrice</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-sm mx-auto">
            Cet espace est réservé à nos ambassadrices & influenceuses partenaires.
            Envie de collaborer avec DocLove ? Contacte-nous 💌
          </p>
          <button
            data-testid="ambassador-back"
            onClick={() => navigate("/app")}
            className="mt-6 inline-flex items-center gap-1.5 text-sm text-pink-400 font-semibold"
          >
            <ArrowLeft size={15} /> Retour
          </button>
        </div>
      </Layout>
    );
  }

  const cards = [
    { label: "Inscriptions", value: data.signups, icon: Users, color: "text-pink-400" },
    { label: "Clientes payantes", value: data.paying, icon: CreditCard, color: "text-emerald-400" },
    { label: "MRR généré", value: `${data.mrr} €`, icon: Euro, color: "text-amber-400" },
  ];

  return (
    <Layout>
      <div className="space-y-6" data-testid="ambassador-page">
        <div>
          <div className="flex items-center gap-2">
            <Megaphone className="text-pink-400" size={22} />
            <p className="text-zinc-500 text-sm">Espace Ambassadrice</p>
          </div>
          <h1 className="font-display text-3xl font-black tracking-tight mt-1">
            Bienvenue {data.name?.split(" ")[0] || ""} 👑
          </h1>
          <p className="text-sm text-zinc-400 mt-1">{data.discount_label}</p>
        </div>

        {/* Lien promo */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="neon-border rounded-2xl bg-zinc-900 p-5 space-y-3"
          data-testid="ambassador-link-card"
        >
          <p className="text-sm font-semibold">Ton lien de parrainage</p>
          <p className="text-xs text-zinc-500">
            Partage-le à ta communauté. Tes filleul·es profitent de <span className="text-pink-300 font-semibold">-50% le 1ᵉʳ mois</span> sur Pro & Docteur.
          </p>
          <div className="flex items-center gap-2">
            <div className="flex-1 rounded-xl bg-zinc-950 border border-zinc-800 px-4 py-3 min-w-0">
              <p className="font-mono text-sm text-pink-300 truncate" data-testid="ambassador-link">{link}</p>
            </div>
            <button
              data-testid="ambassador-copy"
              onClick={copyLink}
              className="rounded-xl bg-zinc-800 hover:bg-zinc-700 px-3 py-3 transition-colors"
            >
              {copied ? <Check size={16} className="text-emerald-400" /> : <Copy size={16} />}
            </button>
            <button
              data-testid="ambassador-share"
              onClick={shareLink}
              className="rounded-xl bg-pink-500 hover:bg-pink-600 text-white px-3 py-3 transition-colors active:scale-95"
            >
              <Share2 size={16} />
            </button>
          </div>
          <div className="flex items-center justify-between rounded-xl bg-zinc-950 border border-zinc-800 px-4 py-2.5">
            <span className="text-xs text-zinc-500">Ton code promo</span>
            <span className="font-mono font-bold tracking-widest text-pink-400">{data.code}</span>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3" data-testid="ambassador-stats">
          {cards.map((c) => (
            <div key={c.label} className="rounded-2xl bg-zinc-900 border border-zinc-800 p-4">
              <c.icon size={16} className={c.color} />
              <p className="font-display text-2xl font-black mt-2">{c.value}</p>
              <p className="text-[11px] uppercase tracking-wider text-zinc-500 mt-0.5">{c.label}</p>
            </div>
          ))}
        </div>

        {/* Filleul·es */}
        <div>
          <h2 className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-3">
            Tes filleul·es ({data.signups})
          </h2>
          {data.referrals?.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-zinc-800 p-8 text-center">
              <Users className="mx-auto text-zinc-600 mb-2" size={22} />
              <p className="text-sm text-zinc-500">Aucune inscription pour l'instant.</p>
              <p className="text-xs text-zinc-600 mt-1">Partage ton lien pour lancer la machine 🚀</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="ambassador-referrals">
              {data.referrals.map((r, i) => (
                <div key={i} className="flex items-center gap-3 rounded-2xl bg-zinc-900 border border-zinc-800 p-4" data-testid={`ambassador-referral-${i}`}>
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-pink-500/30 to-purple-500/30 flex items-center justify-center text-pink-300 font-display font-black shrink-0">
                    {(r.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold truncate">{r.name}</p>
                    <p className="text-xs text-zinc-500">Inscrit·e le {fmt(r.joined_at)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-sm font-bold ${TIER_COLORS[r.tier] || "text-zinc-400"}`}>{r.tier}</p>
                    {r.paying ? (
                      <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2 py-0.5">payante</span>
                    ) : (
                      <span className="text-[10px] text-zinc-600">gratuit</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
